import type { FC, ReactNode } from 'react'
import type { ArticleSummary } from '@/types'

import Link from 'next/link'
import { Reveal } from '@/components/motion/reveal'
import { ArrowCue } from '@/components/site/arrow-link'
import { formatDate } from '@/lib/format'

interface JournalFeaturedProps {
    article: ArticleSummary
}

export const JournalFeatured: FC<JournalFeaturedProps> = ({ article }): ReactNode => {
    return (
        <section
            id='featured'
            className='relative z-[2] flex w-full flex-col gap-8 overflow-clip bg-background px-4 pt-section-sm lg:gap-10 lg:px-6'
        >
            <div className='flex w-full items-end justify-between gap-6 border-b border-border pb-4 text-foreground'>
                <h2 className='t-label text-left'>
                    Worth reading
                </h2>

                {article.topic && (
                    <p
                        data-mdlfy-cms={`topics:${article.topic.id}:name`}
                        className='t-italic text-right'
                    >
                        {article.topic.name}
                    </p>
                )}
            </div>

            <Reveal y={20}>
                <Link
                    href={`/journal/${article.slug}`}
                    className='group/arrow grid w-full grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:gap-12'
                >
                    <div className='relative aspect-[1.5] w-full overflow-hidden bg-muted'>
                        {article.cover && (
                            <img
                                src={article.cover}
                                alt={article.coverAlt ?? ''}
                                loading='lazy'
                                decoding='async'
                                className='absolute inset-0 size-full scale-105 object-cover transition-transform duration-slow ease-studio group-hover/arrow:scale-100'
                            />
                        )}
                    </div>

                    <div className='flex flex-col justify-between gap-10 py-1 text-foreground'>
                        <div className='flex flex-col gap-6'>
                            <p className='t-meta text-left'>
                                <time dateTime={article.publishedOn}>
                                    {formatDate(article.publishedOn)}
                                </time>
                            </p>

                            <h3
                                data-mdlfy-cms={`articles:${article.id}:title`}
                                className='t-name text-left [text-wrap:balance]'
                            >
                                {article.title}
                            </h3>

                            {article.excerpt && (
                                <p
                                    data-mdlfy-cms={`articles:${article.id}:excerpt`}
                                    className='t-body max-w-[520px] text-left text-muted-foreground'
                                >
                                    {article.excerpt}
                                </p>
                            )}
                        </div>

                        <div className='flex flex-col items-start gap-6'>
                            {article.author && (
                                <p className='flex flex-wrap items-center gap-2'>
                                    <span className='t-italic text-umber'>
                                        Written by
                                    </span>

                                    <span
                                        data-mdlfy-cms={`articles:${article.id}:author`}
                                        className='t-body'
                                    >
                                        {article.author}
                                    </span>
                                </p>
                            )}

                            <ArrowCue label="Read the article" />
                        </div>
                    </div>
                </Link>
            </Reveal>
        </section>
    )
}
